export class CustomDate {
  private static padZero(value: any) {
    return value.length < 2 ? '0' + value : value;
  }

  static dateFilter(valueA: string, valueB: string): number {
    const timeA = valueA ? new Date(valueA).getTime() : 0;
    const timeB = valueB ? new Date(valueB).getTime() : 0;
    return timeA - timeB;
  }

  static formatJs(inputDate: string, formatDate: string | null = null): string | null {

    if (typeof inputDate !== 'string') {
      return null;
    }

    const objDate = new Date(inputDate);

    if (isNaN(objDate.getTime())) { return null; }

    const [formattedMonthP, formattedDayP, formattedYearP] = this.convertToFloridaTimezone(objDate);
    const formattedMonth = ('0' + formattedMonthP).slice(-2);
    const formattedDay = ('0' + formattedDayP).slice(-2);
    const formattedYear = formattedYearP;

    let outFormatDate = null;

    switch (formatDate) {
      case 'MM-dd-yyyy':
        outFormatDate = `${formattedMonth}-${formattedDay}-${formattedYear}`;
        break;
      case 'dd-MM-yyyy':
        outFormatDate = `${formattedDay}-${formattedMonth}-${formattedYear}`;
        break;
      case 'yyyy-MM-dd':
        outFormatDate = `${formattedYear}-${formattedMonth}-${formattedDay}`;
        break;
      case 'yyyy-dd-MM':
        outFormatDate = `${formattedYear}-${formattedDay}-${formattedMonth}`;
        break;
      case 'MM-yyyy':
        outFormatDate = `${formattedMonth}-${formattedYear}`;
        break;
      case 'yyyy-MM':
        outFormatDate = `${formattedYear}-${formattedMonth}`;
        break;
      default:
        outFormatDate = `${formattedMonth}-${formattedDay}-${formattedYear}`;
        break;
    }
    return outFormatDate;
  }

  static isoToMonthDayYear(isoDate: string, separator: string = '-'): string {
    const dateOnly = String(isoDate).substring(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return '';
    }
    const parts = dateOnly.split('-');
    return parts[1] + separator + parts[2] + separator + parts[0];
  }

  static formatJsV2(inputDate: string): string {
    const date = new Date(inputDate);
    const year = date.getFullYear();
    const month = this.padZero((date.getMonth() + 1).toString());
    const day = this.padZero(date.getDate().toString());

    return `${year}-${month}-${day}`;
  }

  static formatFromObject(dateObj: {
    year: number;
    month: number;
    day: number;
  }, formatDate: string = 'yyyy-MM-dd'): string | null {
    if (!this.isValidDateObject(dateObj)) {
      return null;
    }
    const { year, month, day } = dateObj;
    const monthStr = this.padZero(month.toString());
    const dayStr = this.padZero(day.toString());

    switch (formatDate) {
      case 'MM-dd-yyyy':
        return `${monthStr}-${dayStr}-${year}`;
      case 'dd-MM-yyyy':
        return `${dayStr}-${monthStr}-${year}`;
      default:
        return `${year}-${monthStr}-${dayStr}`;
    }
  }

  static formatToDatePickerModel(inputDate: string) {
    if (!inputDate || typeof inputDate !== 'string') {
      return null;
    }

    const parsedDate = new Date(inputDate);
    if (isNaN(parsedDate.getTime())) {
      return null;
    }

    const [month, day, year] = this.convertToFloridaTimezone(parsedDate);

    return {
      date: { year, month, day },
      formatted: this.formatJs(inputDate, 'MM-dd-yyyy'),
    };
  }

  static todayIsoDateOnly(): string {
    return this.formatFromObject(this.toDateObject(new Date())) || '';
  }

  static toDateObject(date: Date): {
    year: number;
    month: number;
    day: number;
  } {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  static isValidDateObject(date: {
    year: number;
    month: number;
    day: number;
  }): boolean {
    if (!date || typeof date.year !== 'number' || typeof date.month !== 'number' || typeof date.day !== 'number') {
      return false;
    }const d = new Date(date.year, date.month - 1, date.day);
    return d && d.getFullYear() === date.year && d.getMonth() === date.month - 1 && d.getDate() === date.day;
  }

  static getDate(timeZone = 'America/New_York', format24 = true): string {
    const currentDate = new Date();
    const optionsUS: any = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: !format24
    };
    return currentDate.toLocaleString('en-US', optionsUS);
  }

  static convertToFloridaTimezone(date: Date): [number, number, number] {
    const floridaOffset = -4 * 60;
    const dateOffset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() + (dateOffset - floridaOffset) * 60 * 1000);

    const year = adjustedDate.getFullYear();
    const month = (adjustedDate.getMonth() + 1);
    const day = adjustedDate.getDate();

    return [month, day, year];
  }

  static getNextBusinessDay(input?: Date): string {
    const baseDate = !input ? new Date() : new Date(input);

    if (!baseDate || !baseDate.getTime()) {
      return;
    }

    const FRIDAY = 5;
    const SATURDAY = 6;
    const currentDay = baseDate.getDay();
    let daysToAdd = 1;

    if (currentDay === FRIDAY) {
      daysToAdd = 3;
    } else if (currentDay === SATURDAY) {
      daysToAdd = 2;
    }

    const resultDate = new Date(baseDate);
    resultDate.setDate(resultDate.getDate() + daysToAdd);

    return this.formatJsV2(resultDate.toString());
  }

  static parseLocalDate(date: string): Date {
    const parts = date.split('-').map(Number);
    if (parts[2] > 31) {
      return new Date(parts[2], parts[0] - 1, parts[1]);
    }
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  static formatSecondsToTime(totalSeconds: number): string {
    if (totalSeconds <= 0 || isNaN(totalSeconds)) {
      return '00:00:00';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const hh = ('0' + hours).slice(-2);
    const mm = ('0' + minutes).slice(-2);
    const ss = ('0' + seconds).slice(-2);

    return `${hh}:${mm}:${ss}`;
  }

  static getDateRangeFromFormatted(value: any): string[] | null {
    if (
      !value ||
      typeof value !== 'object' ||
      typeof value.formatted !== 'string'
    ) {
      return null;
    }

    const dates = value.formatted.split(' - ');
    return dates.length === 2 && dates[0] && dates[1] ? dates : null;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/helpers/format-date-helper.ts